import axios, { type AxiosError } from 'axios'
import createAuthRefreshInterceptor, { type AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh'
import type { RefreshTokenResponse } from '@/common/types/api'
import { assetsUrl } from '@/common/lib/assetsUrl'

export const client = axios.create({
    baseURL: assetsUrl,
    withCredentials: true,
})

let refreshToken: string | null = null

export const setHeaderToken = (access_token: string, refresh_token: string) => {
    refreshToken = refresh_token
    client.defaults.headers.common.Authorization = `Bearer ${access_token}`
    if (typeof window !== 'undefined') {
        localStorage.setItem('access_token', access_token)
        localStorage.setItem('refresh_token', refresh_token)
    }
}

export const removeHeaderToken = () => {
    refreshToken = null
    delete client.defaults.headers.common.Authorization
    if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
    }
}

if (typeof window !== 'undefined') {
    const access_token = localStorage.getItem('access_token')
    const refresh_token = localStorage.getItem('refresh_token')
    if (access_token) {
        client.defaults.headers.common.Authorization = `Bearer ${access_token}`
        refreshToken = refresh_token
    }
}

export const refreshTokenRequest = async (refresh_token: string): Promise<RefreshTokenResponse> => {
    const { data } = await client.post<RefreshTokenResponse>('/api/v1/auth/refresh', { refresh_token }, {
        skipAuthRefresh: true,
    } as AxiosAuthRefreshRequestConfig)
    refreshToken = data.refresh_token
    setHeaderToken(data.access_token, data.refresh_token)
    return data
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const refreshAuth = async (failedRequest: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = new Error('error refreshing token') as Error & { response?: any; config?: any }
    err.response = failedRequest.response
    err.config = failedRequest.config

    if (!refreshToken) {
        removeHeaderToken()
        return Promise.reject(err)
    }

    const newToken = await refreshTokenRequest(refreshToken).catch(() => {
        removeHeaderToken()
        return Promise.reject(err)
    })

    if (newToken) {
        failedRequest.response.config.headers.Authorization = `Bearer ${newToken.access_token}`
        setHeaderToken(newToken.access_token, newToken.refresh_token)
        return Promise.resolve(newToken)
    }

    removeHeaderToken()
    return Promise.reject(err)
}

const isChangePasswordRequest = (error: AxiosError): boolean => {
    const url = error.config?.url ?? ''
    return url.includes('/api/v1/auth/change-password')
}

createAuthRefreshInterceptor(client, refreshAuth, {
    statusCodes: [401],
    pauseInstanceWhileRefreshing: true,
    shouldRefresh: error => {
        if (isChangePasswordRequest(error)) {
            return false
        }
        return error.response?.status === 401
    },
})
